// Datasets, in Task Replay's look: the list (each dataset's headline rules at the default settings), and a
// dataset's page: its routing rules, quality against cost, the leaderboard (the combined rating as the headline,
// its judge and own-score parts beside it), composition, tasks and runs; a complexity filter keeps the chart, the
// leaderboard and the tasks to that complexity's tasks.
import { state, esc, get, postStatus } from '../core'
import { sliceLabel, type RoutingRow, type Slice } from '../../shared/replay'
import { heatmap, scoreBar, statusPill, type HmCol, type HmRow } from '../task-replay'
import { openBuilder } from './builder'
import { crumbs, wireCrumbs } from './crumbs'
import { goReplay, openDataset, openResult, openTask, takeAnchor } from './index'
import { openRunDialog } from './rundialog'
import { COMPLEXITY, TYPES, TYPE_COLORS, day, duration, modelName, money, pct, typeName } from './fmt'
import { chartToggle, modelColors, scatter, type ParetoPoint } from './pareto'
import { chip, dot, onThisPage, pageHead, typeChip, wireToc } from './head'
import { conclusion, routingBlock, wireRouting } from './routing'
import { wireTips } from './tip'

interface Headline { evidence: boolean; baseline: string; rules: { slice: Slice; candidate: string; savings: number | null }[]; close: number }
interface DatasetRow {
  id: string; name: string; createdAt: string; tasks: number; byType: Record<string, number>
  byComplexity: Record<string, number>; runs: number; models: string[]; spent: number; headline: Headline
}
interface Rating { elo: number; low: number | null; high: number | null; wins: number; ties: number; losses: number; score?: number | null }
interface ModelStat { model: string; agents: string[]; tasks: number; meanScore: number | null; costPerTask: number | null; secondsPerTask: number | null }
interface TaskRow {
  id: string; title: string; taskType: string; complexity: string; repo: string
  scores: Record<string, number | null>; runs: Record<string, string>; taken: string[]
}
interface Run { id: string; label: string; status: string; createdAt: string; cells: number; done: number; failed: number; models: string[]; spent: number; judging: number }
interface Failure { runId: string; taskId: string; model: string; status: string; reason: string | null }
interface Detail {
  dataset: { id: string; name: string; createdAt: string; tasks: TaskRow[]; runs: Run[]; failures: Failure[] }
  ratings: Record<string, Record<string, Record<string, Rating>>>
  models: ModelStat[]
  modelsByComplexity?: Record<string, ModelStat[]>
  routing: RoutingRow[]
}

let list: DatasetRow[] | null = null
const details = new Map<string, Detail>()
let pareto = true
let labels = true
let lbSort = 'elo'
let lbDir = -1
/** The Tasks tab's filter: a type, a complexity, or both (a composition cell). */
let taskFilter: { type: string; cx: string } = { type: '', cx: '' }
/** The dataset page's complexity ('' for every task), for the dataset it was set on. */
let cxFilter: { id: string; cx: string } = { id: '', cx: '' }
/** A complexity with fewer tasks is not offered: too few for its rating to say anything. */
const MIN_CX_TASKS = 5

// ---- the list -------------------------------------------------------------------

/** Forget what was fetched, so the next paint reads the dataset (and the list) again. */
export function forgetDataset(id?: string): void {
  list = null
  if (id) details.delete(id)
}

export function renderDatasets(root: HTMLElement): void {
  if (list) { paintList(root); return }
  root.innerHTML = '<div class="empty">Loading datasets…</div>'
  get('/api/replay/datasets')
    .then((d: any) => { list = d.datasets; if (!state.rp.id) paintList(root) })
    .catch(() => { root.innerHTML = '<div class="empty">Could not load datasets — is the server reachable?</div>' })
}

function paintList(root: HTMLElement): void {
  const rows = list || []
  const head = '<div class="panel-head tr-l0-head"><div class="tr-l0-title"><h2>Datasets</h2></div>' +
    '<div class="rp-acts"><button type="button" class="btn tr-newsuite" id="rpNew">+ New dataset</button></div></div>'
  const wireNew = () => {
    const b = root.querySelector('#rpNew') as HTMLElement | null
    if (b) b.onclick = () => openBuilder(null, (id) => { forgetDataset(id); openDataset(id) })
  }
  if (!rows.length) { root.innerHTML = '<section class="tr-l0">' + head + '<div class="empty">No v2 datasets yet.</div></section>'; wireNew(); return }
  root.innerHTML = '<section class="tr-l0">' + head +
    '<div class="tr-card tr-card-flat"><table class="tr-suites"><thead><tr>' +
    '<th>Dataset</th><th>Routing</th><th>Tasks</th><th>Models</th><th class="r">Spent</th><th>Created</th></tr></thead><tbody>' +
    rows.map((d) => '<tr class="tr-suiterow" data-id="' + esc(d.id) + '" tabindex="0">' +
      '<td><div class="tr-suite-name">' + esc(d.name) + '<span class="tr-suite-arrow">→</span></div></td>' +
      '<td>' + headline(d.headline) + '</td>' +
      '<td><div class="tr-suite-tasks"><span class="mono">' + d.tasks + '</span><span class="tr-suite-u">tasks</span></div>' + mixBar(d.byType) + '</td>' +
      '<td class="tr-suite-repos">' + esc(d.models.map(modelName).join(', ') || '—') + '</td>' +
      '<td class="r"><span class="mono">' + money(d.spent) + '</span></td>' +
      '<td class="tr-when">' + esc(day(d.createdAt)) + '</td></tr>').join('') +
    '</tbody></table></div></section>'
  Array.prototype.forEach.call(root.querySelectorAll('.tr-suiterow'), (tr: HTMLElement) => {
    const open = () => openDataset(tr.getAttribute('data-id') || '')
    tr.onclick = open
    tr.onkeydown = (e) => { if (e.key === 'Enter') open() }
  })
  wireNew()
  wireTips(root)
}

/** A dataset's rules at the default settings, as Task Replay's picks; or why there are none. */
function headline(h: Headline): string {
  if (!h.evidence) return '<span class="tr-muted">—</span>'
  if (!h.rules.length) return '<span class="tr-muted">no rule' + (h.close ? ' · ' + h.close + ' close' : '') + '</span>'
  return h.rules.slice(0, 2).map((r) =>
    '<div class="rp-pick"><span class="rp-pick-s">' + esc(sliceLabel(r.slice)) + ' →</span> <span class="tr-pick"><span class="tr-pick-m">' +
    esc(modelName(r.candidate)) + '</span><span class="tr-pick-v">−' + pct(r.savings) + '</span></span></div>').join('') +
    (h.rules.length > 2 ? '<div class="tr-muted">+' + (h.rules.length - 2) + ' more</div>' : '')
}

/** Task Replay's thin stacked task-type bar. */
function mixBar(counts: Record<string, number>): string {
  const total = Object.values(counts).reduce((a, n) => a + n, 0) || 1
  return '<div class="tr-mix">' + TYPES.map((t) => t[0]).filter((k) => counts[k]).map((k) =>
    '<span data-tip="' + esc(typeName(k) + '\n' + counts[k] + ' tasks') + '" style="width:' + ((counts[k]! / total) * 100).toFixed(2) +
    '%;background:' + (TYPE_COLORS[k] ?? '#8a857a') + '"></span>').join('') + '</div>'
}

// ---- one dataset ------------------------------------------------------------------

export function renderDataset(root: HTMLElement, id: string): void {
  if (details.has(id)) { paintDataset(root, id); return }
  root.innerHTML = '<div class="empty">Loading the dataset…</div>'
  get('/api/replay/datasets/' + encodeURIComponent(id))
    .then((d: any) => { details.set(id, d); if (state.rp.id === id) paintDataset(root, id) })
    .catch(() => { root.innerHTML = '<div class="empty">Could not load this dataset.</div>' })
}

type View = ReturnType<typeof view>

/** Everything the dataset's sections draw from: over every task, or (`cx`) one complexity's, its fit and the models'
 *  stats over its tasks. A model keeps its colour whatever the filter. */
function view(d: Detail, cx = '') {
  const scope = cx ? 'complexity:' + cx : 'overall'
  const combined = d.ratings.combined?.[scope] ?? {}
  const stats = new Map((cx ? d.modelsByComplexity?.[cx] ?? [] : d.models).map((m) => [m.model, m]))
  const all = [...new Set([...Object.keys(d.ratings.combined?.overall ?? {}), ...d.models.map((m) => m.model)])]
  const models = all.filter((m) => combined[m] || stats.has(m))
    .sort((a, b) => (combined[b]?.elo ?? -Infinity) - (combined[a]?.elo ?? -Infinity))
  return { combined, pairwise: d.ratings.pairwise?.[scope] ?? {}, perCell: d.ratings['per-cell']?.[scope] ?? {}, stats, models, colors: modelColors(all) }
}

function paintDataset(root: HTMLElement, id: string): void {
  const d = details.get(id)!
  const ds = d.dataset
  const v = view(d)
  const trail = [{ label: 'Datasets', go: () => openDataset('') }]
  const live = ds.runs.filter((r) => r.status === 'queued' || r.status === 'running')
  const lastRun = ds.runs.reduce((a, r) => (r.createdAt > a ? r.createdAt : a), '')
  const head = pageHead({
    trail, title: ds.name,
    facts: [
      ds.tasks.length + ' tasks',
      v.models.length ? v.models.length + ' model' + (v.models.length === 1 ? '' : 's') : 'no models run yet',
      money(ds.runs.reduce((a, r) => a + r.spent, 0)) + ' spent',
      lastRun ? 'last run ' + esc(day(lastRun)) : '',
      live.length ? '<a href="javascript:void(0)" class="rp-a rp-live" data-goto="rpRuns">' + live.length + ' run' + (live.length === 1 ? '' : 's') + ' ' +
        (live.some((r) => r.status === 'running') ? 'in progress' : 'queued') + ' · ' +
        live.reduce((a, r) => a + r.done, 0) + '/' + live.reduce((a, r) => a + r.cells, 0) + ' cells</a>' : '',
    ],
    actions: '<button type="button" class="btn" id="rpAdd">Add tasks</button><button type="button" class="btn tr-newsuite" id="rpRun">Run models</button>',
    after: v.models.length ? conclusion(d.routing) : '',
  })
  if (cxFilter.id !== id) cxFilter = { id, cx: '' }
  const cxCount = (c: string) => ds.tasks.filter((t) => t.complexity === c).length
  const cxs = COMPLEXITY.filter((c) => cxCount(c) >= MIN_CX_TASKS && cxCount(c) < ds.tasks.length)
  const seg = cxs.length ? '<span class="tr-muted">Complexity</span><span class="tr-seg" id="rpCx">' +
    [['', 'all', ds.tasks.length] as const, ...cxs.map((c) => [c, c, cxCount(c)] as const)].map(([c, label, n]) =>
      '<button type="button" data-cx="' + esc(c) + '"' + (c === cxFilter.cx ? ' class="on"' : '') + '>' + esc(label) + ' <span class="rp-seg-n">' + n + '</span></button>').join('') +
    '</span>' : ''
  const toc = onThisPage([...(v.models.length ? [['rpRouting', 'Routing'], ['rpLeaderboard', 'Leaderboard']] as [string, string][] : []),
    ['rpTasks', 'Tasks'], ['rpRuns', 'Runs']], seg)
  // one page: the decision and its evidence (only once there are results), the tasks, the runs
  root.innerHTML = '<section class="tr-l0">' + head + toc + '<div id="rpOverview"></div><div id="rpTasks"></div><div id="rpRuns"></div></section>'

  wireCrumbs(root, trail)
  const refresh = () => { forgetDataset(id); if (state.rp.id === id && !state.rp.task) renderDataset(root, id) }
  const run = () => openRunDialog({
    id, name: ds.name, models: v.models,
    tasks: ds.tasks.map((t) => ({ id: t.id, taskType: t.taskType, models: t.taken })),
  }, refresh)
  ;(root.querySelector('#rpRun') as HTMLElement).onclick = run
  ;(root.querySelector('#rpAdd') as HTMLElement).onclick = () => openBuilder(
    { id, name: ds.name, taskIds: ds.tasks.map((t) => t.id), models: v.models },
    () => { forgetDataset(id); get('/api/replay/datasets/' + encodeURIComponent(id)).then((d: any) => { details.set(id, d); renderDataset(root, id); if (v.models.length) run() }) },
  )
  // the chart, the leaderboard and the tasks, at the complexity picked
  const filtered = () => {
    const fv = view(d, cxFilter.cx)
    paintOverview(root.querySelector('#rpOverview') as HTMLElement, d, fv)
    paintTasks(root.querySelector('#rpTasks') as HTMLElement, ds.tasks.filter((t) => !cxFilter.cx || t.complexity === cxFilter.cx), fv)
    wireTips(root)
  }
  Array.prototype.forEach.call(root.querySelectorAll('#rpCx [data-cx]'), (b: HTMLElement) => {
    b.onclick = () => {
      cxFilter = { id, cx: b.getAttribute('data-cx') || '' }
      taskFilter = { type: '', cx: '' }
      root.querySelectorAll('#rpCx [data-cx]').forEach((x) => x.classList.toggle('on', x === b))
      filtered()
    }
  })
  filtered()
  paintRuns(root.querySelector('#rpRuns') as HTMLElement, ds, refresh)
  const goto = root.querySelector('[data-goto]') as HTMLElement | null
  if (goto) goto.onclick = () => root.querySelector('#' + goto.getAttribute('data-goto'))?.scrollIntoView({ behavior: 'smooth' })
  wireToc(root)
  wireTips(root)
  const at = takeAnchor()
  if (at) root.querySelector('#' + at)?.scrollIntoView()
}

/** The decision and its evidence: the routing rules, quality against cost, the leaderboard. */
function paintOverview(pane: HTMLElement, d: Detail, v: View): void {
  if (!d.models.length && !Object.keys(d.ratings.combined?.overall ?? {}).length) { pane.innerHTML = ''; return }
  pane.innerHTML = routingBlock(d.routing) +
    '<div class="tr-block"><div class="tr-block-head"><h3>Quality vs. cost</h3><span class="tr-sub">up &amp; left is better</span>' +
      '<span class="rp-head-right">' + chartToggle(labels, 'rpLabelsBtn', 'Show labels') + chartToggle(pareto, 'rpParetoBtn', 'Show Pareto') + '</span></div>' +
      '<div class="tr-card" id="rpScatter">' + scatter(points(v), v.colors, pareto, labels) + '</div></div>' +
    '<div class="tr-block" id="rpLeaderboard"><div class="tr-block-head"><h3>Model leaderboard</h3><span class="tr-sub">sortable · click a header</span></div>' +
      '<div class="tr-card tr-card-flat"><div id="rpLb">' + leaderboard(v) + '</div></div></div>'
  const flip = (id: string, set: () => boolean) => {
    const b = pane.querySelector('#' + id) as HTMLElement | null
    if (b) b.onclick = () => {
      const on = set()
      b.classList.toggle('on', on)
      b.setAttribute('aria-checked', String(on))
      ;(pane.querySelector('#rpScatter') as HTMLElement).innerHTML = scatter(points(v), v.colors, pareto, labels)
      wireTips(pane)
    }
  }
  flip('rpParetoBtn', () => (pareto = !pareto))
  flip('rpLabelsBtn', () => (labels = !labels))
  wireSort(pane, v)
  wireRouting(pane)
}

/** Composition (a cell, a row or a column filters the grid), then the task × model grid. */
function paintTasks(pane: HTMLElement, all: TaskRow[], v: View): void {
  const f = taskFilter
  const shown = all.filter((t) => (!f.type || t.taskType === f.type) && (!f.cx || t.complexity === f.cx))
  const label = [f.type ? typeName(f.type) : '', f.cx].filter(Boolean).join(' · ')
  pane.innerHTML = '<div class="tr-block"><div class="tr-block-head"><h3>Composition</h3></div>' + composition(all) + '</div>' +
    '<div class="tr-block"><div class="tr-block-head"><h3>Tasks</h3><span class="tr-sub">' +
    (label ? shown.length + ' of ' + all.length + ' · ' + esc(label) + ' · <a href="javascript:void(0)" class="rp-a" data-clear>all tasks</a>' : 'each model’s own score') +
    '</span></div>' + tasks(shown, v) + '</div>'
  Array.prototype.forEach.call(pane.querySelectorAll('[data-ft], [data-fc]'), (el: HTMLElement) => {
    el.onclick = () => {
      const type = el.getAttribute('data-ft') ?? '', cx = el.getAttribute('data-fc') ?? ''
      taskFilter = taskFilter.type === type && taskFilter.cx === cx ? { type: '', cx: '' } : { type, cx }
      paintTasks(pane, all, v)
      wireTips(pane)
    }
  })
  const clear = pane.querySelector('[data-clear]') as HTMLElement | null
  if (clear) clear.onclick = () => { taskFilter = { type: '', cx: '' }; paintTasks(pane, all, v) }
  wireGrid(pane, all)
}

/** Each run: progress, failures (each opens its result), spend; a queued run's local command; cancel (a live run,
 *  or one a restarted worker left running) and resume. */
function paintRuns(pane: HTMLElement, ds: Detail['dataset'], refresh: () => void): void {
  pane.innerHTML = '<div class="tr-block"><div class="tr-block-head"><h3>Runs</h3></div>' +
    runs(ds.runs, ds.failures, new Map(ds.tasks.map((t) => [t.id, t.title || t.id]))) + '</div>'
  Array.prototype.forEach.call(pane.querySelectorAll('[data-fail]'), (a: HTMLElement) => {
    const [run, task, model] = (a.getAttribute('data-fail') || '').split('\n')
    a.onclick = () => openResult(run!, task!, model!)
  })
  Array.prototype.forEach.call(pane.querySelectorAll('[data-run-act]'), (b: HTMLButtonElement) => {
    const [act, id] = (b.getAttribute('data-run-act') || '').split(' ')
    b.onclick = () => {
      b.disabled = true
      postStatus('/api/task-replay/runs/' + encodeURIComponent(id || '') + '/' + act, {})
        .then((r: any) => { if (r.status >= 200 && r.status < 300) refresh(); else { b.disabled = false; b.title = r.body?.error || 'Could not ' + act } })
        .catch(() => { b.disabled = false })
    }
  })
}

function points(v: View): ParetoPoint[] {
  return v.models.filter((m) => v.combined[m] && v.stats.get(m)?.costPerTask).map((m) => ({
    model: m, cost: v.stats.get(m)!.costPerTask!, elo: v.combined[m]!.elo,
    low: v.combined[m]!.low, high: v.combined[m]!.high, tasks: v.stats.get(m)!.tasks,
  }))
}

// -- leaderboard (sortable, client-side) --
const LB_COLS: { key: string; label: string; get: (m: string, v: View) => number | null }[] = [
  { key: 'elo', label: 'Rating', get: (m, v) => v.combined[m]?.elo ?? null },
  { key: 'score', label: 'Own score', get: (m, v) => v.stats.get(m)?.meanScore ?? null },
  { key: 'cost', label: '$/task', get: (m, v) => v.stats.get(m)?.costPerTask ?? null },
  { key: 'time', label: 'Time', get: (m, v) => v.stats.get(m)?.secondsPerTask ?? null },
  { key: 'tasks', label: 'Tasks', get: (m, v) => v.stats.get(m)?.tasks ?? null },
]

function leaderboard(v: View): string {
  const col = LB_COLS.find((c) => c.key === lbSort) ?? LB_COLS[0]!
  const rows = [...v.models].sort((a, b) => ((col.get(a, v) ?? -Infinity) - (col.get(b, v) ?? -Infinity)) * lbDir)
  const elo = (r: Rating | undefined) => (r ? String(Math.round(r.elo)) : '—')
  const cell: Record<string, (m: string) => string> = {
    elo: (m) => {
      const r = v.combined[m]
      const tip = 'rating ' + (r ? Math.round(r.elo) : '—') + '\njudge only ' + elo(v.pairwise[m]) + '\nown scores only ' + elo(v.perCell[m])
      return r ? '<span data-tip="' + esc(tip) + '"><b>' + Math.round(r.elo) + '</b>' +
        (r.low != null && r.high != null && r.high > r.low ? ' <span class="tr-muted">[' + r.low + ', ' + r.high + ']</span>' : '') + '</span>' : '—'
    },
    score: (m) => scoreBar(v.stats.get(m)?.meanScore),
    cost: (m) => money(v.stats.get(m)?.costPerTask),
    time: (m) => duration(v.stats.get(m)?.secondsPerTask),
    tasks: (m) => String(v.stats.get(m)?.tasks ?? '—'),
  }
  const head = '<thead><tr><th class="tr-lb-rank">#</th><th class="tr-lb-variant">Model</th>' +
    LB_COLS.map((c) => '<th class="' + (c.key === 'score' ? '' : 'r ') + (c.key === lbSort ? 'tr-sorted' : '') + '" data-sort="' + c.key + '">' +
      esc(c.label) + ' <span class="tr-ar">' + (c.key === lbSort ? (lbDir < 0 ? '▼' : '▲') : '▲') + '</span></th>').join('') + '</tr></thead>'
  const body = '<tbody>' + rows.map((m, i) => '<tr><td class="tr-lb-rank">' + (i + 1) + '</td>' +
    '<td class="tr-lb-variant"><span class="tr-scatter-vdot rp-lb-dot" style="background:' + (v.colors.get(m) ?? '#9a958b') + '"></span>' +
    '<span class="tr-lb-name">' + esc(modelName(m)) + '</span>' +
    (i === 0 && lbSort === 'elo' && lbDir < 0 && v.combined[m] ? '<span class="tr-tag-best">top</span>' : '') +
    '<div class="tr-lb-sub">' + esc((v.stats.get(m)?.agents ?? []).join(', ')) + '</div></td>' +
    LB_COLS.map((c) => '<td class="' + (c.key === 'score' ? 'tr-lb-scorecell' : 'r') + '">' + cell[c.key]!(m) + '</td>').join('') + '</tr>').join('') + '</tbody>'
  return '<table class="tr-lb2">' + head + body + '</table>' +
    (Object.keys(v.combined).length ? '' : '<div class="tr-note">Not rated yet.</div>')
}

function wireSort(root: HTMLElement, v: View): void {
  Array.prototype.forEach.call(root.querySelectorAll('#rpLb th[data-sort]'), (th: HTMLElement) => {
    th.onclick = () => {
      const key = th.getAttribute('data-sort') || 'elo'
      lbDir = key === lbSort ? -lbDir : key === 'cost' || key === 'time' ? 1 : -1
      lbSort = key
      ;(root.querySelector('#rpLb') as HTMLElement).innerHTML = leaderboard(v)
      wireSort(root, v)
    }
  })
}

/** Composition as filter chips: each type with its count, then its count at each complexity. */
function composition(list_: TaskRow[]): string {
  const types = TYPES.map((t) => t[0]).filter((t) => list_.some((x) => x.taskType === t))
  const cxs = COMPLEXITY.filter((c) => list_.some((x) => x.complexity === c))
  const f = taskFilter
  const on = (type: string, cx: string) => (f.type === type && f.cx === cx ? ' rp-on' : '')
  const btn = (type: string, cx: string, label: string, n: number, cls = '') =>
    '<button type="button" class="rp-fchip' + cls + on(type, cx) + '" data-ft="' + esc(type) + '" data-fc="' + esc(cx) + '">' + label + ' <span>' + n + '</span></button>'
  return '<div class="rp-comp">' + types.map((t) => {
    const mine = list_.filter((x) => x.taskType === t)
    return '<div class="rp-comp-row">' + btn(t, '', '<span class="rp-chip-dot" style="background:' + (TYPE_COLORS[t] ?? '#8a857a') + '"></span>' + esc(typeName(t)), mine.length, ' rp-fchip-type') +
      cxs.map((c) => { const n = mine.filter((x) => x.complexity === c).length; return n ? btn(t, c, esc(c), n) : '' }).join('') + '</div>'
  }).join('') + '</div>'
}

/** Task Replay's heatmap: a row per task, a column per model, each cell the model's own score. Before any model has
 *  run, the tasks as a list. */
function tasks(list_: TaskRow[], v: View): string {
  if (!v.models.length) {
    return '<div class="tr-card tr-card-flat rp-grid rp-scroll"><table class="tr-lb2"><thead><tr><th>Task</th><th>Type</th><th>Complexity</th><th>Repo</th></tr></thead><tbody>' +
      list_.map((t) => '<tr><th class="tr-hm-rt" data-r="' + esc(t.id) + '">' + esc(t.title || t.id) + '</th><td>' + esc(typeName(t.taskType)) + '</td><td>' +
        esc(t.complexity) + '</td><td>' + esc(t.repo) + '</td></tr>').join('') + '</tbody></table></div>'
  }
  const clip = (s: string) => (s.length > 70 ? s.slice(0, 69) + '…' : s)
  const rows: HmRow[] = list_.map((t) => ({ key: t.id, label: clip(t.title || t.id) }))
  const cols: HmCol[] = v.models.map((m) => ({ key: m, label: modelName(m) }))
  const byId = new Map(list_.map((t) => [t.id, t]))
  return '<div class="tr-card rp-grid"><div class="rp-scroll rp-hm-box">' + heatmap(rows, cols, (rk, ck) => byId.get(rk)?.scores[ck] ?? null, 'Task', { tieWins: false }) + '</div>' +
    '<div class="tr-hm-legend"><span>lower</span><span class="tr-hm-ramp"></span><span>higher</span><span class="tr-hm-legsp">▢ best in row</span></div></div>'
}

function wireGrid(root: HTMLElement, list_: TaskRow[]): void {
  const byId = new Map(list_.map((t) => [t.id, t]))
  Array.prototype.forEach.call(root.querySelectorAll('.rp-grid th[data-r]'), (th: HTMLElement) => {
    th.classList.add('rp-link')
    th.onclick = () => openTask(th.getAttribute('data-r') || '')
  })
  Array.prototype.forEach.call(root.querySelectorAll('.rp-grid td[data-r]'), (td: HTMLElement) => {
    const t = byId.get(td.getAttribute('data-r') || '')
    const m = td.getAttribute('data-c') || ''
    const run = t?.runs[m]
    if (!t || !run) return
    td.classList.add('rp-link')
    td.onclick = () => openResult(run, t.id, m)
  })
}

function runs(list_: Run[], failures: Failure[], titles: Map<string, string>): string {
  if (!list_.length) return '<div class="empty">No runs yet.</div>'
  const failed = (id: string) => failures.filter((f) => f.runId === id)
  const live = (r: Run) => r.status === 'queued' || r.status === 'running'
  // a run stays running while its cells are judged
  const status = (r: Run) => r.status === 'running' && r.cells > 0 && r.done + r.failed >= r.cells ? 'judging' : r.status
  const act = (r: Run) => live(r) ? '<button type="button" class="btn" data-run-act="cancel ' + esc(r.id) + '">Cancel</button>'
    : r.done < r.cells ? '<button type="button" class="btn" data-run-act="resume ' + esc(r.id) + '">Resume</button>' : ''
  return '<div class="tr-card tr-card-flat"><table class="tr-runs"><thead><tr><th>Label</th><th>Status</th><th>Models</th>' +
    '<th class="tr-run-metric">Cells</th><th class="tr-run-metric">Failed</th><th class="tr-run-metric">Spent</th><th>Started</th><th></th></tr></thead><tbody>' +
    list_.map((r) => {
      const fs = failed(r.id)
      return '<tr><td class="tr-run-label">' + esc(r.label || r.id.slice(0, 8)) +
        (r.status === 'queued' ? '<div class="rp-cmd mono" title="Run it on this machine, from eval/">.venv/bin/python -m taskreplay.run --run-id ' + esc(r.id) + '</div>' : '') +
        (fs.length ? '<details class="rp-fails"><summary>' + fs.length + ' failed</summary>' + fs.map((f) =>
          '<div><a href="javascript:void(0)" class="rp-a" data-fail="' + esc(f.runId + '\n' + f.taskId + '\n' + f.model) + '">' +
          esc(modelName(f.model)) + ' · ' + esc((titles.get(f.taskId) || f.taskId).slice(0, 80)) + '</a>' +
          (f.reason ? '<div class="tr-muted rp-fail-why">' + esc(f.reason.slice(0, 200)) + '</div>' : '') + '</div>').join('') + '</details>' : '') +
        '</td><td>' + statusPill(r.status, status(r)) + '</td>' +
        '<td class="tr-suite-repos">' + esc(r.models.map(modelName).join(', ')) + '</td>' +
        '<td class="tr-run-metric num">' + r.done + ' / ' + r.cells + '</td><td class="tr-run-metric num">' + (r.failed || '') + '</td>' +
        '<td class="tr-run-metric num"' + (r.judging ? ' title="cells ' + money(r.spent - r.judging) + ' · judging ' + money(r.judging) + '"' : '') + '>' +
        money(r.spent) + '</td><td class="tr-when">' + esc(day(r.createdAt)) + '</td>' +
        '<td>' + act(r) + '</td></tr>'
    }).join('') + '</tbody></table></div>'
}
